'use client';
import React from 'react';
import { Button, HeaderLogoSVG, MainHero, Heading } from '@ui';
import { IconLinkedIn, IconImage } from '@icons';
import {
  heroLine,
  readHeroCtas,
  readHeroImages,
  resolveHeroCtaHref,
  type LayoutLocale,
} from '@lib/heroLocale';
import { useRouter } from 'next/navigation';
import HeroImageRotator from './HeroImageRotator';

interface HeroWrapperProps {
  isSpanish?: boolean;
}

const HeroWrapper: React.FC<HeroWrapperProps> = ({ isSpanish = false }) => {
  const router = useRouter();
  const locale: LayoutLocale = isSpanish ? 'es' : 'en';
  const ns = 'home';
  const heroImages = readHeroImages(ns, locale);
  const heroCtas = readHeroCtas(ns, locale);
  const [rowCta1, rowCta2, ...cardCtas] = heroCtas;
  const cardCta = cardCtas[0];

  return (
    <MainHero
      main={
        <>
          <Heading
            as='h1'
            eyebrow={heroLine('hero_eyebrow', ns, locale)}
            title={heroLine('hero_title', ns, locale)}
            description={heroLine('hero_description', ns, locale)}
            descriptionClassName='hero-sub'
            subtitle={heroLine('hero_subtitle', ns, locale)}
          />
          <div className='row btn-row'>
            {rowCta1 ? (
              <Button
                className='btn-primary'
                as='a'
                href={resolveHeroCtaHref(rowCta1.hero_cta_url, locale)}
                label={rowCta1.hero_cta}
                icon={<HeaderLogoSVG />}
                iconPosition='right'
              />
            ) : null}
            {rowCta2 ? (
              <Button
                className='btn-secondary'
                as='a'
                href={rowCta2.hero_cta_url}
                label={rowCta2.hero_cta}
                target={rowCta2.hero_cta_url.startsWith('http') ? '_blank' : undefined}
                rel={rowCta2.hero_cta_url.startsWith('http') ? 'noopener noreferrer' : undefined}
                icon={rowCta2.hero_cta_url.includes('linkedin.com') ? <IconLinkedIn /> : undefined}
                iconPosition='right'
              />
            ) : null}
          </div>
        </>
      }
      card={
        <>
          <HeroImageRotator images={heroImages} />
          {cardCta ? (
            <div
              className='hero-card-link'
              role='link'
              tabIndex={0}
              aria-label={cardCta.hero_cta}
              onClick={() => router.push(resolveHeroCtaHref(cardCta.hero_cta_url, locale))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  router.push(resolveHeroCtaHref(cardCta.hero_cta_url, locale));
                }
              }}
            >
              <span className='btn btn-primary hero-card-link-label' aria-hidden>
                {cardCta.hero_cta} <IconImage />
              </span>
            </div>
          ) : null}
        </>
      }
    />
  );
};

export default HeroWrapper;
